<template>
  <keep-alive>
    <header>
      <Grid variant="detail" class="dossier-hero">
        <div
          class="dossier-hero-container is--full-detail-width-small is--full-detail-width-medium is--full-detail-width-large"
        >
          <DynamicBackgroundImage
            v-if="props.image"
            :image="props.image"
            :renditions="computedRenditions"
            class="dossier-hero-image-overlay"
          />
          <div class="dossier-hero-content">
            <Label type="icon" title="Dossier" icon="eenvandaag-general:copy-04" />
            <div class="dossier-hero-text-content">
              <Heading
                v-if="props.title"
                :level="1"
                :content="props.title"
                class="dossier-hero-title"
              />
              <span v-if="props.adjustTime" class="dossier-hero-time-container">
                Laatst bijgewerkt:
                <time class="dossier-hero-time">{{ state.adjustTime }}</time></span
              >
            </div>
          </div>
        </div>
        <div
          v-if="props.description"
          class="dossier-hero-description is--full-detail-width-medium is--full-detail-width-large has--padding"
        >
          <span>{{ props.description }}</span>
        </div>
      </Grid>
    </header>
  </keep-alive>
</template>

<script setup lang="ts">
import type { ICmsDossiers } from '~~/types/_cms-types';
import type { IImageRendition } from '~~/types/local_types';
import formatUtils from '~/utils/formatUtils';

const props = defineProps<ICmsDossiers>();

const computedRenditions = computed(() => {
  const renditions: IImageRendition[] = [
    { breakpoint: 'sm', size: 480, aspectRatio: '1x1' },
    { breakpoint: 'md', size: 800, aspectRatio: '16x9' },
    { breakpoint: 'lg', size: 1024, aspectRatio: '16x9' },
  ];

  return renditions;
});

const { formatDateWithFullMonth } = formatUtils();

const state = reactive({
  adjustTime: props.adjustTime && formatDateWithFullMonth(props.adjustTime),
});
</script>

<style scoped lang="postcss">
.dossier-hero {
  --dossier-hero-background-color: hsl(var(--color-primary-950));
  background-color: var(--dossier-hero-background-color);

  overflow: hidden;

  @media (--md-viewport) {
    --dossier-hero-background-color: inherit;
  }
}

.dossier-hero-container {
  background-color: hsl(var(--color-primary-950));
  display: flex;
  position: relative;
  width: 100%;

  @media (--md-viewport) {
    min-height: 356px;
  }
}

.dossier-hero-image-overlay {
  width: 100%;
  position: absolute;
  aspect-ratio: 1 / 1;

  &::before {
    background-position: center;
  }

  &::after {
    position: absolute;
    content: '';
    height: 100%;
    width: 100%;
    left: 0;
    bottom: -15%;
    background: linear-gradient(
      0deg,
      hsl(var(--color-primary-950)) 25.42%,
      rgba(0, 0, 0, 0) 68.16%
    );
  }

  @media (--md-viewport) {
    right: 0;
    height: 100%;
    width: 67%;

    &::after {
      content: none;
    }
    &::before {
      mask-image: linear-gradient(270deg, #000 30%, rgba(0, 0, 0, 0) 99.88%);
    }
  }
}

.dossier-hero-content {
  --dossier-hero-content-padding: var(--unit-32) var(--unit-32) var(--unit-6);

  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: self-start;
  padding: var(--dossier-hero-content-padding);
  width: 100%;

  @media (--md-viewport) {
    --dossier-hero-content-padding: var(--unit-32);
  }

  @media (--lg-viewport) {
    --dossier-hero-content-padding: var(--unit-48);
  }
}

.dossier-hero-text-content {
  display: flex;
  flex-direction: column;
  gap: var(--unit-8);
  margin-top: 75%;

  @media (--md-viewport) {
    margin-top: auto;
    max-width: 60%;
  }
}

.dossier-hero-title {
  @add-mixin heading-3xl;

  color: hsl(var(--color-neutral-white));
}

.dossier-hero-time-container {
  color: hsl(var(--color-primary-200));

  @add-mixin label-lg;
}

.dossier-hero-time {
  color: hsl(var(--color-neutral-white));

  @add-mixin label-lg-semi-bold;
}

.dossier-hero-description {
  --dossier-hero-description-color: hsl(var(--color-primary-50));
  --dossier-hero-description-padding: 0 var(--unit-14) var(--unit-40);

  @add-mixin body-md;
  z-index: 10;
  color: var(--dossier-hero-description-color);
  padding: var(--dossier-hero-description-padding);

  @media (--md-viewport) {
    --dossier-hero-description-color: hsl(var(--color-primary-900));
    --dossier-hero-description-padding: 0;
  }
}
</style>
